import BaseService from "./BaseService";
import GeoService from "./GeoService";
import Request from "./Request";
import MapService from "./MapService";
import AppStorage from "../storage/AppStorage";
import AppService from "./AppService";
import i18n from "i18next";
import {ModalComponentService} from "./ModalComponentService";
import LogManager from "./LogManager";
import PermissionsService from "./PermissionsService";

/** Destino de una ruta a pie. El geofield entrega las coordenadas como cadena. */
export interface DirectionDestination {
    lat: string | number;
    lon: string | number;
}

/** Ruta tal y como la devuelve la Directions API de Mapbox. */
interface MapboxRoute {
    distance: number;
    duration: number;
    geometry: GeoJSON.LineString;
}

/** Respuesta de la Directions API. */
interface MapboxDirectionsResponse {
    code?: string;
    routes?: MapboxRoute[];
}


export class DirectionService extends BaseService {
    static MAPBOX_DIRECTIONS_URL = 'https://api.mapbox.com/directions/v5/mapbox/';

    static normalizeValues(value: string | number): number {
        return Number(Number(value).toFixed(6));
    }

    static async gotToDestination(destination: DirectionDestination): Promise<void> {
        AppService.setLoadingState(true);
        try {
            const hasPermission = await PermissionsService.ensureGeoPermission();
            if (!hasPermission) {
                // ensureGeoPermission ya muestra el modal de permisos correspondiente
                return;
            }
            const currentLocation = await GeoService.getCurrentLocation();
            if (!currentLocation) {
                this.debug('gotToDestination: no se pudo obtener la geolocalización');
                LogManager.createError(i18n.t('dialog.geoLocationError'));
                return;
            }
            const profile = 'walking';
            const mapbox_access_token =  this.config().mapbox_access_token;
            const coordinates =
                `${this.normalizeValues(currentLocation.lng)},${this.normalizeValues(currentLocation.lat)};${this.normalizeValues(destination.lon)},${this.normalizeValues(destination.lat)}`;
            const url = `${this.MAPBOX_DIRECTIONS_URL}${profile}/${coordinates}?geometries=geojson&overview=full&access_token=${mapbox_access_token}`;
            const apiResponse = await Request.get<MapboxDirectionsResponse>(url, false);
            this.debug('Resultado de búsqueda', apiResponse);
            if (apiResponse.code === 'Ok') {
                if (apiResponse.routes!.length > 0) {
                    await this.drawWalkingRoute(apiResponse.routes![0]);
                    MapService.centerBounds(apiResponse.routes![0].geometry.coordinates);
                    if (!AppStorage.getDirectionClicked()) {
                        this.showDisclaimer();
                    }
                } else {
                    this.debug('gotToDestination: no se encontraron rutas');
                }
            } else {
                ModalComponentService.dialogText = i18n.t('dialog.error');
                ModalComponentService.openModalComponent(ModalComponentService.ModalComponentTypes.DISCLAIMER_CARD);
            }
        } catch (e) {
            this.debug('gotToDestination error', e);
        } finally {
            AppService.setLoadingState(false);
        }
    }

    static async drawWalkingRoute(route: MapboxRoute): Promise<void> {
        this.debug('Received route: ', route);
        MapService.createRouteToLocationLayer(route.geometry);
        MapService.createDirectionInfo(route.distance, route.duration);
    }

    static async deleteWalkingRoute(): Promise<void> {
        MapService.deleteWalkingRoute();
    }

    static showDisclaimer(): void {
        this.debug('Show disclaimer');
        ModalComponentService.dialogText = i18n.t('dialog.message');
        ModalComponentService.openModalComponent(ModalComponentService.ModalComponentTypes.DISCLAIMER_CARD);

        AppStorage.setDirectionClicked(true);
    }
}
